import { ReactElement } from "react";
import { Box, ButtonBase, SxProps, Theme, Typography } from "@mui/material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DetailedRound } from "../../../models/round";
import TheGameScoreCard from "./thegamescorecard";
import TheGameLiveMarket from "./thegamelivemarket";
import TheGamePrizePlan from "./thegameprizeplan";
import { TheGameManifest } from "./thegamemanifest";
import { cardSx, getMarketStates, mainSx, pageSx, rowShares, sectionTitleSx, supportiveSx, tileBackground } from "./thegamecommon";

const playButtonSx: SxProps<Theme> = {
  height: 44,
  borderRadius: "var(--button-border-radius)",
  fontFamily: "var(--font-button, var(--font-body))",
  fontSize: 16,
  fontWeight: 500,
  textTransform: "uppercase",
  background: "var(--button-color)",
  color: "var(--button-text-color)",
};

interface TheGameSpectatorViewProps {
  round: DetailedRound;
  manifest: TheGameManifest;
}

// Rows-still-perfect and payout-per-row from the design need backend data and are left out for v1.
export default function TheGameSpectatorView({ round, manifest }: TheGameSpectatorViewProps): ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const states = getMarketStates(round, manifest, []);
  const isFinished = round.status === "FINISHED" || (states.length > 0 && states.every((s) => s.decided));
  const hasShares = round.fixtures.some((f) => rowShares(f) !== undefined);

  return (
    <Box sx={pageSx}>
      <Box component="main" sx={mainSx}>
        <TheGameScoreCard round={round} manifest={manifest} states={states} />

        <Box
          component="section"
          sx={{ ...cardSx, background: tileBackground(), padding: "16px", display: "flex", flexDirection: "column", gap: "12px" } as SxProps<Theme>}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <TimerOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontFamily: "var(--font-body)", fontSize: 16, fontWeight: 600 }}>
              {isFinished ? t("tg_match_over") : t("betting_closed_live")}
            </Typography>
          </Box>
          <ButtonBase type="button" sx={playButtonSx} onClick={() => history.push("/")}>
            {t("play_next_round")}
          </ButtonBase>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", paddingTop: "4px" }}>
          <Typography component="h2" sx={sectionTitleSx}>{t("markets")}</Typography>
          {hasShares && <Typography sx={{ ...supportiveSx, fontSize: 14 } as SxProps<Theme>}>{t("rows_spread_hint")}</Typography>}
        </Box>

        {manifest.markets.map((market) =>
          states[market.pos] ? (
            <TheGameLiveMarket
              key={market.pos}
              market={market}
              state={states[market.pos]}
              showResult={false}
              shares={rowShares(round.fixtures[market.pos])}
            />
          ) : null
        )}

        <TheGamePrizePlan round={round} numLegs={states.length} />
      </Box>
    </Box>
  );
}
